import { create } from "zustand";
import { supabase } from "@/lib/supabase";
import { SupabaseShareEntityViewEnum } from "@/lib/supabase/supabaseTypes";
import { JobStatusEnum } from "@/lib/types/job";
import { Logger } from "@/lib/logger/Logger";
import { produce } from "immer";
import {
	JobListJob,
	JobRequestChannel,
	VendorSharedFile,
} from "@/lib/types/job-list.types";
import { usePatientStore } from "../Patient";
import { useCentralStore } from "../Central";

const OPEN_STATUSES = [JobStatusEnum.IN_PROGRESS, JobStatusEnum.COMPLETED];

const COMPLETED_STATUSES = [
	JobStatusEnum.BOOKED_SINGLE,
	JobStatusEnum.BOOKED_MONTHLY,
	JobStatusEnum.PAID_SINGLE,
	JobStatusEnum.PAID_MONTHLY,
];

type DocumentType = "qt" | "dn" | "cn";

interface GroupedFiles {
	qt: VendorSharedFile[];
	dn: VendorSharedFile[];
	cn: VendorSharedFile[];
}

interface VendorJobListState {
	requestedChannels: JobRequestChannel[];
	deniedChannels: JobRequestChannel[];
	acceptedJobs: JobListJob[];
	sharedFiles: VendorSharedFile[];
	currentVendorOrgId: string | null;
	loading: boolean;
	activeFileId: string | null;
}

const initialState: VendorJobListState = {
	requestedChannels: [],
	deniedChannels: [],
	acceptedJobs: [],
	sharedFiles: [],
	currentVendorOrgId: null,
	loading: false,
	activeFileId: null,
};

export interface VendorJobListSlice extends VendorJobListState {
	fetchVendorData: (
		vendorOrgId: string,
		customerOrgId: string
	) => Promise<void>;
	getGroupedFiles: () => {
		inArbeit: GroupedFiles;
		abgeschlossen: GroupedFiles;
	};
	setActiveFileId: (fileId: string | null) => void;
	resetVendorJobList: () => void;
}

export const useVendorJobStore = create<VendorJobListSlice>()((set, get) => ({
	...initialState,

	fetchVendorData: async (vendorOrgId: string, customerOrgId: string) => {
		if (!vendorOrgId || !customerOrgId) return;

		if (get().currentVendorOrgId !== vendorOrgId) {
			useCentralStore.setState((state) => ({
				vendorFetchEpoch: state.vendorFetchEpoch + 1,
			}));
			set({
				...initialState,
				currentVendorOrgId: vendorOrgId,
				loading: true,
			});
		} else {
			set({ loading: true });
		}

		const epoch = useCentralStore.getState().vendorFetchEpoch;

		const [sharedFilesResult, jobsResult, requestedResult, deniedResult] =
			await Promise.all([
				supabase.rpc("get_vendor_shared_files", {
					p_vendor_org_id: vendorOrgId,
					p_customer_org_id: customerOrgId,
				}),
				// jobResults is for finding jobs that are accepted but don't have files yet ("Akzeptiert" bucket)
				supabase
					.from(SupabaseShareEntityViewEnum.JOBS_WITH_SHARES)
					.select("*")
					.eq("status", JobStatusEnum.IN_PROGRESS)
					.eq("organization_id", vendorOrgId)
					.eq("shared_with", customerOrgId)
					.order("created_at", { ascending: false }),
				supabase
					.from("message_channels")
					.select(
						"id, job_title, customer_org_id, vendor_org_id, unread_count, last_message_at"
					)
					.eq("channel_type", "job_request")
					.eq("vendor_org_id", vendorOrgId)
					.eq("customer_org_id", customerOrgId)
					.is("job_id", null)
					.order("last_message_at", { ascending: false }),
				supabase
					.from("message_channels")
					.select(
						"id, job_title, customer_org_id, vendor_org_id, unread_count, last_message_at"
					)
					.eq("channel_type", "job_request_denied")
					.eq("vendor_org_id", vendorOrgId)
					.eq("customer_org_id", customerOrgId)
					.order("last_message_at", { ascending: false }),
			]);

		if (sharedFilesResult.error) {
			Logger.error(
				sharedFilesResult.error,
				{},
				"Error fetching vendor shared files"
			);
		}
		if (jobsResult.error) {
			Logger.error(jobsResult.error, {}, "Error fetching vendor jobs");
		}
		if (requestedResult.error) {
			Logger.error(
				requestedResult.error,
				{},
				"Error fetching vendor request channels"
			);
		}
		if (deniedResult.error) {
			Logger.error(
				deniedResult.error,
				{},
				"Error fetching vendor denied channels"
			);
		}

		const sharedFiles: VendorSharedFile[] = (
			sharedFilesResult.data ?? []
		).map((f: any) => ({
			file_id: f.file_id,
			file_name: f.file_name,
			file_path_name: f.file_path_name,
			file_bucket_name: f.file_bucket_name,
			file_created_at: f.file_created_at,
			file_meta_data: f.file_meta_data,
			job_id: f.job_id,
			job_code: f.job_code,
			job_title: f.job_title,
			job_status: f.job_status,
			patient_first_name: f.patient_first_name,
			patient_last_name: f.patient_last_name,
			patient_title: f.patient_title,
			document_type: f.document_type,
			document_date: f.document_date,
		}));

		const jobIdsWithFiles = new Set(sharedFiles.map((f) => f.job_id));

		const mapJob = (job: any): JobListJob => ({
			id: job.id,
			title: job.title,
			code: job.code,
			status: job.status?.toString() ?? null,
			created_at: job.created_at,
			patient_id: job.patient_id,
			vendor_org_id: job.organization_id,
			shared_with: job.shared_with,
			job_document_types: job.job_document_types,
		});

		// Any job that has no file in the files is only "accepted"
		const acceptedJobs = (jobsResult.data ?? [])
			.filter((j) => !jobIdsWithFiles.has(j.id!))
			.map(mapJob);

		const acceptedPatientIds = Array.from(
			new Set(
				acceptedJobs
					.map((j) => j.patient_id)
					.filter((id): id is string => !!id)
			)
		);
		if (acceptedPatientIds.length > 0) {
			await usePatientStore
				.getState()
				.fetchPatientsByIds(acceptedPatientIds);
		}

		if (useCentralStore.getState().vendorFetchEpoch !== epoch) return;

		const mapChannels = (data: any[]): JobRequestChannel[] =>
			data.map((c) => ({
				channelId: c.id!,
				title: c.job_title,
				created_at: c.last_message_at,
				customerOrgId: c.customer_org_id,
				vendorOrgId: c.vendor_org_id,
				unreadCount: c.unread_count ?? 0,
				lastMessageContent: c.last_message_content ?? null,
				lastMessageCreatedBy: c.last_message_created_by ?? null,
			}));

		set(
			produce((state: VendorJobListState) => {
				state.sharedFiles = sharedFiles;
				state.acceptedJobs = acceptedJobs;
				state.requestedChannels = mapChannels(
					requestedResult.data ?? []
				);
				state.deniedChannels = mapChannels(deniedResult.data ?? []);
				state.currentVendorOrgId = vendorOrgId;
				state.loading = false;
			})
		);
	},

	getGroupedFiles: () => {
		const files = get().sharedFiles;
		const emptyGroup = (): GroupedFiles => ({ qt: [], dn: [], cn: [] });
		const inArbeit = emptyGroup();
		const abgeschlossen = emptyGroup();

		for (const file of files) {
			const docType = file.document_type as DocumentType;
			if (!inArbeit[docType]) continue;

			if (OPEN_STATUSES.includes(file.job_status)) {
				inArbeit[docType].push(file);
			} else if (COMPLETED_STATUSES.includes(file.job_status)) {
				abgeschlossen[docType].push(file);
			}
		}

		return { inArbeit, abgeschlossen };
	},

	setActiveFileId: (fileId: string | null) => {
		set({ activeFileId: fileId });
	},

	resetVendorJobList: () => {
		set(initialState);
	},
}));
